import { useNavigate } from "react-router-dom"

import { SearchableExecutionRunRef } from "@models/executions"

import { Badge } from "@ui/badge"

interface AgentRunnerConversationHistoryButtonProps {
    searchableRun: SearchableExecutionRunRef
}
export function AgentRunnerConversationHistoryButton({ searchableRun }: Readonly<AgentRunnerConversationHistoryButtonProps>) {
    const navigate = useNavigate()

    const name = searchableRun.name
    const run = searchableRun.run

    const handleOnOpen = () => {
        const hashId = extractHashId(searchableRun)
        if (!hashId) {
            return
        }
        if (!window.location.pathname.includes("assistant")) {
            navigate(`/assistant${hashId}`, { replace: true })
        } else {
            window.location.hash = hashId
            window.location.reload()
        }
    }

    return (
        <div key={run.id} className="flex flex-col w-full hover:cursor-pointer p-1 rounded-lg hover:bg-gray-100" onClick={handleOnOpen}>
            <div className="flex flex-row flex-1 items-center gap-1 min-w-0">
                {searchableRun.status === "to_review" && (
                    <span className="relative flex size-3">
                        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-500 opacity-75"></span>
                        <span className="relative inline-flex size-3 rounded-full bg-green-400"></span>
                    </span>
                )}
                {searchableRun.status === "in_progress" && (
                    <span className="relative flex size-3">
                        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-orange-500 opacity-75"></span>
                        <span className="relative inline-flex size-3 rounded-full bg-orange-400"></span>
                    </span>
                )}
                <p className="text-sm font-bold truncate w-full">{name}</p>
                <Badge className="text-xs" variant={"outline"}>
                    {searchableRun.access}
                </Badge>
            </div>
            <p className="text-xs text-muted-foreground">{timestampToDate(new Date(searchableRun.updated_at).getTime() / 1000)}</p>
        </div>
    )
}

function timestampToDate(timestamp: number) {
    if (!timestamp) {
        return "Unknown"
    }

    const date = new Date(timestamp * 1000)
    const currentDate = new Date()

    const diffMs = currentDate.getTime() - date.getTime()
    const diffSeconds = Math.floor(diffMs / 1000)
    const diffMinutes = Math.floor(diffSeconds / 60)
    const diffHours = Math.floor(diffMinutes / 60)
    const diffDays = Math.floor(diffHours / 24)
    const diffWeeks = Math.floor(diffDays / 7)
    const diffMonths = Math.floor(diffDays / 30)
    const diffYears = Math.floor(diffDays / 365)

    if (diffSeconds < 60) {
        return diffSeconds === 1 ? "1 second ago" : `${diffSeconds} seconds ago`
    } else if (diffMinutes < 60) {
        return diffMinutes === 1 ? "1 minute ago" : `${diffMinutes} minutes ago`
    } else if (diffHours < 24) {
        return diffHours === 1 ? "1 hour ago" : `${diffHours} hours ago`
    } else if (diffDays <= 7) {
        return diffDays === 1 ? "1 day ago" : `${diffDays} days ago`
    } else if (diffWeeks <= 4) {
        return diffWeeks === 1 ? "1 week ago" : `${diffWeeks} weeks ago`
    } else if (diffMonths < 12) {
        return diffMonths === 1 ? "1 month ago" : `${diffMonths} months ago`
    }

    return diffYears === 1 ? "1 year ago" : `${diffYears} years ago`
}

function extractHashId(searchableRun: SearchableExecutionRunRef) {
    if (searchableRun.run_id && searchableRun.workflow_id) {
        return `#${searchableRun.run_id + "__" + searchableRun.workflow_id}`
    }
    const chatTag = searchableRun.run.tags?.find((tag) => tag.startsWith("chat:"))
    if (chatTag) {
        return `#${chatTag.replace("chat:", "")}`
    }
    return `#${searchableRun.run.tags?.find((tag) => tag.startsWith("service:"))?.replace("service:", "")}`
}
